import { forwardRef, useMemo } from 'react';
import { MemberUser } from '@youhelp-ru/core';
import { DialogMessageViewModel } from '@common/model/dialog/message/dialogMessageViewModel';
import { MessageType } from '@common/model/dialog/message/messageType';
import { PropsWithFalgs, PropsWithSlots } from '@cp/ds';
import { Stack } from '@cp/ds/src/components/stack';
import { DialogViewModel } from '@common/model/dialog/dialogViewModel';
import { DealViewModel } from '@common/model/deal';
import { DealMessageContextProvider, DealMessageContextType } from '../model/DealMessageContext';
import { DealMessageMarkAsRead, DealMessagePreviews, DealMessageWidgets } from '../lib/elements';
import { useSlotsRenderer } from '../model/useSlotsRenderer';
import { DealMessageFlags, DealMessageSlots } from '../model/types';
import { useGetCatalogItemByClientIdQuery } from '../../catalogItem/@x/deal';
import { coreDealApi } from '../api';
import { DealCardEntity } from './DealCard';

interface DealMessageRendererProps extends PropsWithSlots<DealMessageSlots>, PropsWithFalgs<DealMessageFlags> {
  isPreview?: boolean;
  deal?: DealViewModel;
  dialog: DialogViewModel;
  currentMember: MemberUser;
  participatingMember?: MemberUser;
  message: DialogMessageViewModel<MessageType.DealProposition>;
}

export const defaultFlags: Record<DealMessageFlags, boolean> = {
  markAsRead: true,
};

export const DealMessageRenderer = forwardRef<HTMLDivElement, DealMessageRendererProps>(
  ({ currentMember: member, message, isPreview, dialog, slots, flags: propFlags, participatingMember, deal: propDeal }, ref) => {
    const flags = useMemo(() => ({ ...defaultFlags, ...propFlags }), [propFlags]);

    const slotsRenderer = useSlotsRenderer(slots);

    const queryDeal = coreDealApi.useGetOneQuery(
      {
        dealId: message.payload.dealId,
      },
      {
        refetchOnFocus: false,
        refetchOnMountOrArgChange: true,
        skip: !message?.payload?.dealId || !!propDeal,
      },
    );

    const deal = useMemo(() => propDeal ?? queryDeal?.data, [propDeal, queryDeal]);

    const { data: specialist } = useGetCatalogItemByClientIdQuery(
      { clientId: deal?.subjectProviderClientId ?? 0 },
      {
        skip: !deal,
        refetchOnFocus: false,
        refetchOnMountOrArgChange: true,
      },
    );

    const context = useMemo<DealMessageContextType | null>(() => {
      if (!deal || !specialist) {
        return null;
      }

      const currentMember = participatingMember || member;

      return {
        flags,

        deal,
        dialog,
        message,
        specialist,
        currentMember,

        isCreatedFromVacancy: deal?.isCreatedFromVacancy,
        isInitiatedByEmployer: deal?.initiatorClientId === deal?.subjectConsumerClientId,
        isAuthor: currentMember.id === message.author.entityId,
        isInitiator: deal?.initiatorClientId === currentMember.id,
        isProvider: deal?.subjectProviderClientId === currentMember.id,
        isConsumer: deal?.subjectConsumerClientId === currentMember.id,
        isPreview: isPreview || message.payload.dealStatus !== deal?.status,
        isExternalMember: ![deal?.subjectConsumerClientId, deal?.subjectProviderClientId].includes(member.id ?? 0),

        slotsRenderer,
      };
    }, [deal, specialist, participatingMember, member, flags, dialog, message, isPreview, slotsRenderer]);

    const MessageView = useMemo(() => {
      if (context?.isPreview) {
        return DealMessagePreviews[message.payload.dealStatus];
      }
      return DealMessageWidgets[message.payload.dealStatus];
    }, [context?.isPreview, message.payload.dealStatus]);

    if (!context) {
      return null;
    }

    return (
      <DealMessageContextProvider value={context}>
        <Stack alignItems={context.isAuthor || isPreview ? 'flex-end' : 'flex-start'} ref={ref} spacing={0.6}>
          <MessageView />
          {!context.isPreview && slotsRenderer('footer')}
        </Stack>
        {flags.markAsRead && <DealMessageMarkAsRead />}
      </DealMessageContextProvider>
    );
  },
);
